import { useEffect, useRef, useState, useMemo } from "react";
import { allowOrWarn, Util } from '../../util/Util';
import Invoice from "../invoice/InvoiceReport";
import PaymentModal from "./PaymentModal";
import BulkPaymentModal from "./BulkPaymentModal";
import { useData } from "../../hooks/useData";
import { useUI } from "../../providers/MoleUIContext";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useDateRange, periodRequestConfig } from "../../hooks/useDateRange";
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { isChangeTenderMode, LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AddCircleOutline, Delete, DeleteOutline, Edit, ReceiptOutlined, Refresh } from "@mui/icons-material";
import TableView from "../../components/TableView";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MRT_ColumnDef } from "material-react-table";
import ExportMenuButton from "../../components/ExportMenuButton";
import { createSignCell } from "../../util/cellFormatters";
import { useAppSocket } from "../../providers/AppSocketContext";
import { parseISODay } from "../../util/businessClock";
import ReceiptDialog from "../../components/ReceiptDialog";
import { ReceiptSource } from "../../util/receiptPdf";

import { roundQty } from "../../util/quantity";
import { canAmend, canDrop, canPrintReceipt, canRecord } from '../../util/access';
interface Props {
	bundle?: any;
	hideColumns?: string[];
	onCallback?: () => void;
}

/**
 * A receipt is for money handed over: cash and similar tenders, never a refund,
 * a wallet or voucher spend, or a discount. Rows cached before `mode_id` was
 * sent are let through; the backend applies the same rule.
 */
const isReceiptablePayment = (row: any) =>
	Util.compareStrings(row?.y_type, "Payment")
	&& (row?.mode_id == null || isChangeTenderMode(row.mode_id));

const Payment = ({ bundle, hideColumns, onCallback }: Props) => {
	const [toEdit, setToEdit] = useState<any>(null);
	const [toDelete, setToDelete] = useState<any>(null);
	const [toDeleteBulk, setToDeleteBulk] = useState<any[]>([]);
	const [toView, setToView] = useState<any>(null);
	const [newRecord, setNewRecord] = useState<boolean>(false);
	const [receiptSource, setReceiptSource] = useState<ReceiptSource | null>(null);

	const { api, session, access } = useAuth();
	const { appSettings } = useAppSocket();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const mayPrintReceipt = canPrintReceipt(access);
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading, setData } = useData(LocalStorageKeys.PAYMENT_RECORDS);
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(bundle ? undefined : LocalStorageKeys.PAYMENT_RECORDS);

	const paymentMade = useRef(false);
	const toastIdRef = useRef<string | number>(-1);
	const tableRef = useRef<any>(null);
	const firstFetchRef = useRef(false);

	useEffect(() => {
		return () => {
			// Runs just before the component is destroyed
			if (paymentMade.current) {
				onCallback?.();
			}
		};
	}, []);

	useEffect(() => {
		if (!bundle && !loadFromIndexedDB() && period && !firstFetchRef.current) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, [period]);

	useEffect(() => {
		if (bundle) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, [bundle]);

	const fetchData = async (useLoadModal: boolean = true) => {
		setLoading(true);

		const res = await api.get(`/payments/${bundle ? bundle.date : ""}`, {
			...(!bundle && periodRequestConfig(period))
		});

		if (res) {
			if (res.status === 200) {
				if (bundle) {
					setData(res.data);
				} else {
					saveFromServer(res.data, period);
				}
				toast.update(toastIdRef.current, {
					render: t("common:toast.success.refresh"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
				toast.dismiss(toastIdRef.current);
			}
		} else {
			toast.dismiss(toastIdRef.current);
		}

		setLoading(false);
	};

	const handleDelete = async () => {
		if (toDelete) {
			setLoadingMessage("DELETE");

			const res = await api.delete(`/payments/${toDelete.id}`);

			if (res.status === 204) {
				toastIdRef.current = toast.loading(`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`);
				fetchData();
			} else if (res.status === 409) {
				toast.error(t("common:toast.error.deposited"), { autoClose: 3000 });
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(handleDelete);
			}

			setLoadingMessage(null);
			setToDelete(null);
		}
	};

	const handleBulkDelete = async () => {
		if (toDeleteBulk.length > 0) {
			const payload = { batches: toDeleteBulk };

			setLoadingMessage("DELETE");

			const res = await api.post("/payments/delete", payload);

			if (res) {
				if (res.status === 204) {
					toastIdRef.current = toast.loading(`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`);
					tableRef.current?.resetRowSelection();
					fetchData();
				} else if (res.status === 409) {
					toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
				} else if (res.status === 401 || res.status === 422) {
					queueReRun(handleBulkDelete);
				}
			}

			setLoadingMessage(null);
			setToDeleteBulk([]);
		}
	};

	// Define table columns
	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.date"),
			id: "date",
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), "medium"), //render Date as a string
			filterVariant: 'date-range',
		},
		{
			header: t("common:tables.agent"),
			accessorKey: "agent",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.client"),
			accessorKey: "client",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.amount"),
			accessorKey: "amount",
			Cell: createSignCell({ signStyle: "accounting" }),			
			filterFn: 'contains',
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: 'sum', //show the max age in the group (lots of pre-built aggregationFns to choose from)
			//required to render an aggregated cell
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{' '}
					{table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}:{' '}
					<Box
						sx={{ color: 'info.main', display: 'inline', fontWeight: 'bold' }}
					>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			// Add Footer for Grand Total
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);
				const [isCalculating, setIsCalculating] = useState(false);

				useEffect(() => {
					setIsCalculating(true);
					setTotal(0);

					const rows = table.getFilteredRowModel().rows;
					// run in a micro delay, allowing paint before heavy computation
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.amount);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
						setIsCalculating(false);
					}, 0); // <— yields to browser first

					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: 'bold', color: 'success.main' }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.entryType"),
			accessorKey: "y_type",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.mode"),
			accessorKey: "mode",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.type"),
			accessorKey: "x_type",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.invoiceNo"),
			accessorKey: "reference",
			Cell: ({ cell }: { cell: any }) => (
				<button
					style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", textDecoration: "underline" }}
					onClick={() => setToView(cell.row.original)}
				>
					{cell.getValue()}
				</button>
			),
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.user"),
			accessorKey: "user",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.pos"),
			accessorKey: "depot",
			filterVariant: 'autocomplete',
			filterFn: 'contains',
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
	], [t]);

	// Hide specified columns
	if (hideColumns) {
		columns.forEach((column) => {
			if (hideColumns.includes(column.accessorKey || "")) {
				columns.splice(columns.indexOf(column), 1);
				column.header = "";
			}
		}
		);
	}

	const handlePaymentSuccess = () => {
		if (toEdit) {
			toastIdRef.current = toast.loading(`${t("common:toast.success.update")} ${t("common:toast.loading.refresh")}`);
		} else {
			toastIdRef.current = toast.error(t("common:toast.loading.changes"));
		}
		fetchData();
	};

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
				stateKey={bundle ? undefined : LocalStorageKeys.PAYMENT_RECORDS}
				posting="payment"
					dataScope="payment"
					header={t("common:payments.headers.records")}
					data={data}
					columns={columns}
					totals={[{ key: "amount", label: t("common:tables.amount") }]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={setPeriod}
					isLoading={loading}
					bundle={bundle}
					getRowSx={(row) => Util.compareStrings(row.original?.y_type || "", "Refund") ? {
						backgroundColor: (theme: any) => theme.palette.mode === 'dark'
							? 'rgba(255, 167, 38, 0.12)'
							: 'rgba(255, 167, 38, 0.08)',
						borderLeft: '3px solid',
						borderLeftColor: 'warning.main',
					} : {}}

					renderTopToolbarActions={(table) => (
						<Box sx={{ display: 'flex', gap: '0.5rem' }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							<Tooltip title={t("common:ui.tooltips.newPayment")}>
								<IconButton
									onClick={() => {
										if (allowOrWarn(canRecord(access, 'payment')) || allowOrWarn(canRecord(access, 'refund'))) {
											setNewRecord(true);
										}
									}}
									color="success">
									<AddCircleOutline />
								</IconButton>
							</Tooltip>
							{
								[
									...new Set(
										table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch)
									),
								].length === 1 && (
									<Tooltip title={t("common:ui.tooltips.editSelectedPayment")}>
										<IconButton
											onClick={() => {
												if (Util.compareStrings(table.getSelectedRowModel().flatRows[0].original.y_type, "Payment") && allowOrWarn(canAmend(access, 'payment'))) {
													setToEdit(table.getSelectedRowModel().flatRows[0].original);
												} else if (Util.compareStrings(table.getSelectedRowModel().flatRows[0].original.y_type, "Refund") && allowOrWarn(canAmend(access, 'refund'))) {
													setToEdit(table.getSelectedRowModel().flatRows[0].original);
												}
											}}
											color="primary">
											<Edit />
										</IconButton>
									</Tooltip>
								)
							}
							{mayPrintReceipt && (table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
								<Tooltip title={t("common:receipt.issueSelected")}>
									<IconButton
										onClick={() => {
											const ids = table.getSelectedRowModel().flatRows
												.filter((row: any) => isReceiptablePayment(row.original))
												.map((row: any) => Number(row.original.id));
											if (ids.length > 0) setReceiptSource({ kind: "payments", ids });
										}}
										color="primary">
										<ReceiptOutlined />
									</IconButton>
								</Tooltip>
							)}
							{table.getIsSomeRowsSelected() && (
								<Tooltip title={t("common:ui.tooltips.deleteSelection")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canDrop(access, 'payment', 'delete_bulk'))) {
												const rowsBatches = [
													...new Set(
														table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch)
													),
												];
												if (rowsBatches.length === 1) {
													setToDelete(table.getSelectedRowModel().flatRows[0].original);
												} else {
													tableRef.current = table;

													setToDeleteBulk(rowsBatches);
												}
											}
										}}
										color="error">
										<Delete />
									</IconButton>
								</Tooltip>
							)}
							<ExportMenuButton
								prefix="payments"
								data={data}
								columns={columns}
								table={table}
							/>
						</Box>
					)}
					renderRowActionMenuItems={(row, closeMenu) => [
						mayPrintReceipt && !(toDelete || toEdit) && isReceiptablePayment(row.original) ? (
							<MenuItem
								key="receipt"
								onClick={() => {
									setReceiptSource({ kind: "payments", ids: [Number(row.original.id)] });
									closeMenu();
								}}
							>
								<ListItemIcon>
									<ReceiptOutlined />
								</ListItemIcon>
								{t("common:receipt.action")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit) ? (
							<MenuItem
								key={0}
								onClick={() => {
									if (Util.compareStrings(row.original.y_type, "Payment")) {
										if (allowOrWarn(canAmend(access, 'payment'))) {
											setToEdit(row.original);
										}
									} else {
										if (allowOrWarn(canAmend(access, 'refund'))) {
											setToEdit(row.original);
										}
									}
									closeMenu();
								}}
								sx={{
									color: 'primary.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'primary.main',
								}}>
									<Edit />
								</ListItemIcon>
								{t("common:ui.buttons.edit")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit) ? (
							<MenuItem
								key={1}
								onClick={() => {
									if (allowOrWarn(canDrop(access, 'payment'))) {
										setToDelete(row.original);
									}
									closeMenu();
								}}
								sx={{
									color: 'error.main',
								}}
							>
								<ListItemIcon sx={{
									color: 'error.main',
								}}>
									<Delete />
								</ListItemIcon>
								{t("common:ui.buttons.delete")}
							</MenuItem>
						) : null,
					]}
				/>

			</LocalizationProvider>

			{/* Invoice Modal */
				toView &&
				<Invoice
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
				/>
			}

			<ReceiptDialog source={receiptSource} onClose={() => setReceiptSource(null)} />

			{newRecord && (
				<BulkPaymentModal
					isOpen={newRecord}
					toggle={() => setNewRecord(false)}
					onSuccess={handlePaymentSuccess}
				/>
			)}

			{toEdit && (
				<PaymentModal
					isOpen={!!toEdit}
					toggle={() => setToEdit(null)}
					bundle={toEdit}
					editMode
					onSuccess={handlePaymentSuccess}
				/>
			)}

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				<ConfirmDialog
					onConfirm={toDelete ? handleDelete : handleBulkDelete}
					onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]) }}
					isOpen={toDelete || toDeleteBulk.length > 0}
					setIsOpen={() => { }}
					title={<><DeleteOutline sx={{ color: "red" }} /> {t("common:dialog.deleteConfirmation")}</>}
					message={
						toDelete ?
							<>
								{t("common:payments.confirm.deleteSingleAmount")}&nbsp;
								<b><u>{Util.formatCurrency(toDelete?.amount, appSettings?.BIZ_CURRENCY?.value || "USD")}</u></b>&nbsp;
								{t("common:payments.confirm.deleteSingleDate")} <b><u>{Util.formatDate(toDelete?.date, "full")}</u></b>&nbsp;
								{t("common:payments.confirm.deleteSingleClient")} <b><u>{toDelete?.client}</u></b>!
							</>
							:
							<>
								{t("common:payments.confirm.deleteBulk", { count: Number(toDeleteBulk.length) })}
							</>
					}
					confirmBtnColor="error"
					confirmBtnVariant="contained"
					confirmBtnText={<>{t("common:ui.buttons.delete")}</>}
				/>
			}

		</>
	);
};

export default Payment;
